import { makeRoute } from "@flow/common/route";
import * as Safe from "safe-portals";
import { Serializers } from "@flow/common/types";
import * as perm from "@flow/common/core_permissions";
import {
  signatureListedSerializer,
  petitionListedSerializer,
  recentSignatureListedSerializer,
  duplicateSignatureItemSerializer,
} from "./types";

const petitionSettingsSerializer = Safe.obj({
  consent_config_guids: Safe.optional(Safe.array(Safe.str)),
  default_consent_config_guids: Safe.array(Safe.uuid),
  default_thank_you_email_template: Safe.str,
  default_decision_notification_email_template: Safe.str,
});

export type PetitionSettings = Safe.TypeIn<typeof petitionSettingsSerializer>;

export const getSettings = makeRoute(
  "/admin/api/petition/get_settings",
  [perm.VIEW_SETTINGS],
  Safe.nothing,
  petitionSettingsSerializer
);

export const setSettings = makeRoute(
  "/admin/api/petition/set_settings",
  [perm.UPDATE_SETTINGS],
  Safe.obj({
    consent_config_guids: Safe.optional(Safe.array(Safe.str)),
    default_thank_you_email_template: Safe.str,
    default_decision_notification_email_template: Safe.str,
  }),
  Safe.nothing
);

const signPetitionDataSerializer = Safe.obj({
  use_prefill_member_data: Safe.bool,
  context: Serializers.journeyContext,
  actionGuid: Safe.str,
  hideSignature: Safe.bool,
  consents: Safe.array(Serializers.consent),
  petitionTitle: Safe.str,
  captchaToken: Safe.optional(Safe.str),
  variantIdx: Safe.int,
  campaignTargetUniqueIds: Safe.optional(Safe.array(Safe.str)),
  comment: Safe.optional(Safe.str),
});
export type SignPetitionData = Safe.TypeIn<typeof signPetitionDataSerializer>;

export const signPetition = makeRoute("/api/plugin/petition/sign", "public", signPetitionDataSerializer, Safe.nothing);

export const getSignatures = makeRoute(
  "/admin/api/plugin/petition/signatures",
  [],
  // arguments
  Safe.obj({
    page: Safe.int,
    rowsPerPage: Safe.int,
    petitionGuid: Safe.str,
    search: Safe.optional(Safe.str),
  }),
  // response
  Safe.array(signatureListedSerializer)
);

export const getPetitions = makeRoute(
  "/admin/api/plugin/petition/petitions",
  [],
  // arguments
  Safe.obj({
    page: Safe.int,
    rowsPerPage: Safe.int,
    search: Safe.optional(Safe.str),
  }),
  // response
  Safe.array(petitionListedSerializer)
);

/**
 * Endpoint Parameter Type - Delete a signature.
 *
 * Polymorphic type for the arguments to the deleteSignature endpoint. This supports deletion by signature guid, or
 * by petition guid and member email.
 */
const deleteSignatureArgsSerializer = Safe.variant(
  "signature_guid_ident",
  Safe.obj({
    guid: Safe.str,
  }),
  "petition_guid_and_member_email_ident",
  Safe.obj({
    petitionGuid: Safe.str,
    memberEmail: Safe.str,
  })
);

export type DeleteSignatureArgsType = Safe.TypeIn<typeof deleteSignatureArgsSerializer>;

/**
 * Endpoint - Delete a signature.
 *
 * This method has two invocation variants. It can be invoked with a signature guid, or with a petitionGuid and
 * memberEmail. It must be invoked with one of these two variants.
 *
 * @param guid - The guid of the signature to delete.
 * or
 * @param petitionGuid - The guid of the petition to delete the signature from.
 * @param memberEmail - The email of the member to delete the signature from.
 */
export const deleteSignature = makeRoute(
  "/admin/api/plugin/petition/signature/delete",
  [],
  // arguments
  deleteSignatureArgsSerializer,
  // response
  Safe.nothing
);

export const downloadSignatures = makeRoute(
  "/admin/api/plugin/petition/signature/download",
  [],
  Safe.obj({
    petitionGuid: Safe.str,
    actionTitle: Safe.str,
    allFields: Safe.bool,
  }),
  Safe.optional(Safe.str)
);

export const getRecentSignatures = makeRoute(
  "/api/plugin/petition/signatures/recent",
  "public",
  // arguments
  Safe.obj({
    petitionGuid: Safe.str,
  }),
  // response
  Safe.array(recentSignatureListedSerializer)
);

export const renderDecisionNotificationEmailPreview = makeRoute(
  "/admin/api/plugin/petition/decision_notification_email_preview",
  [],
  Safe.obj({
    from: Safe.str,
    subject: Safe.str,
    body: Safe.str,
    petitionTitle: Safe.str,
    signatures: Safe.str,
    decisionMakerName: Safe.str,
    petitionUrl: Safe.str,
    template: Safe.optional(Safe.str),
  }),
  Safe.Result.serializer({
    ok: Safe.obj({ subject: Safe.str, body: Safe.str }),
    error: Safe.str,
  })
);

export const getDuplicateSignatures = makeRoute(
  "/admin/api/plugin/petition/signatures/duplicates",
  [],
  // arguments
  Safe.obj({
    page: Safe.int,
    rowsPerPage: Safe.int,
    petitionGuid: Safe.str,
    search: Safe.optional(Safe.str),
  }),
  // response
  Safe.array(duplicateSignatureItemSerializer)
);

export const getTotalVsUniqueSignatureStats = makeRoute(
  "/admin/api/plugin/petition/signatures/duplicates/totals",
  [],
  Safe.obj({
    petitionGuid: Safe.str,
  }),
  Safe.obj({
    total: Safe.int,
    unique: Safe.int,
  })
);
